import type { TFunction, i18n as I18n } from 'i18next';
import type { NotificationType } from '@unipostudio/shared-types';
import { Icons } from '@/shared/constants/icons';
import type { Notification } from '@/shared/services/notifications.service';
import { localizeNotification } from './notification-text';
import { localizedOccasionName } from '@unipostudio/platform-constants';

type IconComponent = typeof Icons.info;

/**
 * Every type's icon and colour, for the bell and the history. A Record over
 * the shared NotificationType list, so a new type fails the typecheck here
 * instead of showing as a grey generic bell (N21).
 */
export const NOTIFICATION_ICONS: Record<NotificationType, IconComponent> = {
  post_published: Icons.check,
  post_failed: Icons.alert,
  post_at_risk: Icons.warning,
  post_gone: Icons.alert,
  post_scheduled: Icons.calendar,
  video_ready: Icons.video,
  approval_requested: Icons.clock,
  approval_decided: Icons.check,
  approval_needs_time: Icons.clock,
  new_comment: Icons.comment,
  new_dm: Icons.inbox,
  mention: Icons.comment,
  sla_breach: Icons.alert,
  inbox_escalated: Icons.alert,
  inbox_assigned: Icons.inbox,
  inbox_reply_failed: Icons.alert,
  token_expiring: Icons.channels,
  team_invite: Icons.team,
  team_invite_accepted: Icons.team,
  renewal_upcoming: Icons.billing,
  refund_completed: Icons.billing,
  plan_upgraded: Icons.billing,
  plan_expiring: Icons.billing,
  payment_failed: Icons.alert,
  plan_halted: Icons.alert,
  plan_changed: Icons.billing,
  plan_ended: Icons.billing,
  cancel_confirmed: Icons.billing,
  refund_declined: Icons.billing,
  plan_limits_paused: Icons.warning,
  export_ready: Icons.download,
  trial_ending: Icons.billing,
  trial_ended: Icons.billing,
  plan_ready: Icons.sparkles,
  weekly_report: Icons.analytics,
  occasion_upcoming: Icons.calendar,
  test: Icons.notification,
  system: Icons.info,
  publish_success: Icons.check,
  publish_failure: Icons.alert,
  inbox_message: Icons.comment,
  inbox_reply: Icons.send,
  analytics_report: Icons.analytics,
  subscription: Icons.billing,
};

export const NOTIFICATION_ICON_COLORS: Record<NotificationType, string> = {
  post_published: 'var(--good)',
  post_failed: 'var(--bad)',
  post_at_risk: 'var(--warn)',
  post_gone: 'var(--bad)',
  post_scheduled: 'var(--ink-2)',
  video_ready: 'var(--ai-text)',
  approval_requested: 'var(--warn)',
  approval_decided: 'var(--good)',
  approval_needs_time: 'var(--warn)',
  new_comment: 'var(--ink-2)',
  new_dm: 'var(--ink-2)',
  mention: 'var(--ink-2)',
  sla_breach: 'var(--warn)',
  inbox_escalated: 'var(--warn)',
  inbox_assigned: 'var(--ink-2)',
  inbox_reply_failed: 'var(--bad)',
  token_expiring: 'var(--warn)',
  team_invite: 'var(--ink-2)',
  team_invite_accepted: 'var(--good)',
  renewal_upcoming: 'var(--ink-2)',
  refund_completed: 'var(--ink-2)',
  plan_upgraded: 'var(--ink-2)',
  plan_expiring: 'var(--warn)',
  payment_failed: 'var(--bad)',
  plan_halted: 'var(--bad)',
  plan_changed: 'var(--ink-2)',
  plan_ended: 'var(--warn)',
  cancel_confirmed: 'var(--ink-2)',
  refund_declined: 'var(--warn)',
  plan_limits_paused: 'var(--warn)',
  export_ready: 'var(--good)',
  trial_ending: 'var(--ink-2)',
  trial_ended: 'var(--ink-2)',
  plan_ready: 'var(--ai)',
  weekly_report: 'var(--human-text)',
  occasion_upcoming: 'var(--color-accent-text)',
  test: 'var(--ink-2)',
  system: 'var(--ink-3)',
  publish_success: 'var(--good)',
  publish_failure: 'var(--bad)',
  inbox_message: 'var(--ink-2)',
  inbox_reply: 'var(--ink-2)',
  analytics_report: 'var(--ink-2)',
  subscription: 'var(--ink-2)',
};

export function notificationIcon(type: string): IconComponent {
  return (NOTIFICATION_ICONS as Record<string, IconComponent | undefined>)[type] ?? Icons.notification;
}

export function notificationIconColor(type: string): string {
  return (NOTIFICATION_ICON_COLORS as Record<string, string | undefined>)[type] ?? 'var(--ink-3)';
}

/**
 * Occasion reminders are written by the server in English; the details they
 * carry (name, days to go) are enough to say it in the reader's language.
 */
function occasionOf(n: Notification, language: string): { name: string; emoji: string; inDays: number; posters: boolean } | null {
  if (n.type !== 'occasion_upcoming' || !n.data) return null;
  const { occasionId, occasionName, occasionEmoji, inDays, posters } = n.data as Record<string, unknown>;
  if (typeof occasionName !== 'string' || typeof inDays !== 'number') return null;
  // The name in the reader's language where we have it (PC-39c).
  const name = typeof occasionId === 'string' ? localizedOccasionName(occasionId, occasionName, language) : occasionName;
  return { name, emoji: typeof occasionEmoji === 'string' ? occasionEmoji : '', inDays, posters: posters !== false };
}

/**
 * A notification's title and body in the reader's language: occasions from
 * their details, anything carrying `data.i18n` from its key, anything else
 * as stored.
 */
export function notificationText(n: Notification, t: TFunction, i18n: Pick<I18n, 'exists'> & { language?: string }): { title: string; body: string } {
  const o = occasionOf(n, i18n.language ?? 'en');
  if (o) {
    return {
      title: t('notifications.occasion.title', { emoji: o.emoji, name: o.name, count: o.inDays }),
      // Starter makes no posters, so its reminder does not offer one (AL-33).
      body: t(o.posters ? 'notifications.occasion.body' : 'notifications.occasion.bodyNoPoster'),
    };
  }
  return localizeNotification(n, t, i18n);
}
